import { useState } from "react";
import { supabase } from "../lib/supabase";

/* HIS 2026-09-10 SWITCH, ON THE WEB (W3, 2026-09-18).
 *
 * *"ON applies and publishes next week with no accept step."* One column on the relationship's own
 * row — `coach_clients.auto_apply_next_week`.
 *
 * ⚠️ **THE GUARD IS THE DATABASE'S, NOT THIS COMPONENT'S.** The migration refuses ON for a PENDING
 * or ENDED relationship (42501), and the update policy admits only the coach's own rows. So this
 * does not re-decide who may switch it: it sends the change and shows the server's own sentence if
 * it is refused. A second copy of that rule here is the one that would drift.
 *
 * ⚠️ **OFF IS NEVER REFUSED, and that asymmetry is deliberate** — a coach must always be able to
 * stop a machine writing to their athlete, whatever state the relationship is in.
 *
 * 🔴 **AND ONE THING IT DOES NOT DO, SAID ON ITS FACE RATHER THAN DISCOVERED.** Turning this ON
 * does not make next week appear from the web. The writer that fills it is
 * `CoachNextWeekService` — Swift, on the phone — and its only trigger is a coach opening the
 * iOS Clients screen (`ClientsView.swift:283`; the scheduled endpoint was never built, only the
 * fallback). A coach who lives on the web would switch this on and wait for a week that never
 * arrives. The sentence under the control says so, because a switch that silently does nothing is
 * worse than no switch.
 */
export function AutoApplySwitch({ linkId, initial, canEnable }: {
  /** `coach_clients.id` — the relationship, not the client. */
  linkId: string;
  initial: boolean;
  /** Active AND accepted. The database enforces it; this is only what the UI should offer. */
  canEnable: boolean;
}) {
  const [on, setOn] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function toggle(next: boolean) {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase
      .from("coach_clients")
      .update({ auto_apply_next_week: next })
      .eq("id", linkId);
    setBusy(false);
    if (err !== null) {
      // The server's own sentence — a 42501 here means the relationship is not one this may be
      // switched on for, and paraphrasing it would hide which of several reasons applied.
      setError(err.message);
      return;
    }
    setOn(next);
  }

  return (
    <div className="autoapply">
      {/* 🔴 W21 (B2) — A SWITCH, AND THE CLASS IS WHAT MAKES IT ONE. `appearance: none` on the
          checkbox plus a track and a thumb at phone width; the box stays a real `<input
          type="checkbox">` so it is still a checkbox to a keyboard and to a screen reader.
          `role="switch"` was considered and refused: it changes what is announced ("on"/"off"
          rather than "checked") for no behaviour the coach gains, and the app's own control is a
          SwiftUI `Toggle`, which is a checkbox by another name. */}
      <label className="autoapply-row">
        <input
          type="checkbox"
          checked={on}
          disabled={busy || (!on && !canEnable)}
          onChange={(e) => { void toggle(e.target.checked); }}
        />
        <span className="ht-callout">Write next week automatically</span>
      </label>
      {/* 🔴 W72 STEP 7 — ONE SENTENCE, EVERY WIDTH (the owed item: "its paragraph cut to one sentence that still says the
          phone writes it"). The state is the SWITCH's to say; the sentence says what ON does and WHEN. F15 step 3.7: the week is
          written by the server (`apply-next-week`) when the coach opens Coachwork — this web app's shell or the phone — so
          "by the app on your phone, not this browser" became false and went. The app's own ON line ("Tap the assigned days
          to check it") is still not copied: there is no accept step to tap. */}
      <p className="ht-caption autoapply-said">
        Next week is written from what they logged, with no accept step, when you next open Coachwork.
      </p>
      {!on && !canEnable && (
        <p className="ht-caption autoapply-note">
          Available once this client has accepted and the relationship is active.
        </p>
      )}
      {error !== null && <p className="ht-callout autoapply-error" role="alert">{error}</p>}
    </div>
  );
}
